<script lang="ts">
  import { onMount, untrack } from "svelte";
  import { createRadarEventsSocket, frontendApi, isAuthExpired, parseRadarState, type RadarStateResponse } from "./api";
  import { activeMeetingCount, readAppBadgePreference, syncAppBadge, writeAppBadgePreference } from "./app-badge";
  import { consumeOAuthCompletion, isOAuthCompletionMessage, isStandaloneDisplayMode, launchGoogleOAuth, watchOAuthPopup } from "./auth";
  import AuthScreen from "./components/AuthScreen.svelte";
  import LegalScreen from "./components/LegalScreen.svelte";
  import PortraitGuard from "./components/PortraitGuard.svelte";
  import RadarStateScreen from "./components/RadarStateScreen.svelte";
  import SettingsScreen from "./components/SettingsScreen.svelte";
  import { legalPageForPath, type AppFixture, type BootstrapState, type IntegrationUpdate, type Mode, type RadarConnection, type RadarMeeting, type RadarPerson, type RadarRefreshState, type RadarViewState, type SettingsViewState, type Skin } from "./model";
  import { browserPush, createPushOperationCoordinator, disablePushNotifications, enablePushNotifications, inspectPushNotifications, pushOwnerKey, signOutWithPushCleanup, testPushNotifications, type PushApi, type PushNotificationState } from "./push";
  import { browserRadarConnectionEnvironment, RadarConnectionManager } from "./radar-connection";
  import { browserHardRefreshEnvironment, HardRefreshManager } from "./hard-refresh";
  import { LifecycleGuard } from "./lifecycle";
  import { RadarSequenceGate } from "./radar-sequence";

  let { fixture, onFixtureChange }: { fixture?: AppFixture; onFixtureChange?: (patch: Partial<AppFixture>) => void } = $props();
  const initialFixture = untrack(() => fixture);
  const legalPage = legalPageForPath(typeof location === "undefined" ? "/" : location.pathname);
  let loadedGitCommit = $derived(fixture?.buildGitCommit ?? __BUILD_GIT_COMMIT__);
  const lifecycle = new LifecycleGuard();
  const radarSequence = new RadarSequenceGate();

  let bootstrap = $state<BootstrapState | null>(initialFixture?.bootstrap ?? null);
  let bootstrapError = $state<string | null>(initialFixture?.error ?? null);
  let meetings = $state<RadarMeeting[]>(initialFixture?.meetings ?? []);
  let people = $state<RadarPerson[]>(initialFixture?.people ?? []);
  let radarState = $state<RadarViewState>(initialFixture?.radarState ?? "loading");
  let refreshState = $state<RadarRefreshState>(initialFixture?.refreshState ?? "idle");
  let connection = $state<RadarConnection>(initialFixture?.connection ?? "reconnecting");
  let settingsOpen = $state(initialFixture?.settingsOpen ?? false);
  let settingsState = $state<SettingsViewState>(initialFixture?.settingsState ?? "configured");
  let pushState = $state<PushNotificationState>(initialFixture?.pushState ?? "default");
  let pushMessage = $state<string | undefined>(initialFixture?.pushMessage);
  let mode = $state<Mode>(initialFixture?.mode ?? "dark");
  let skin = $state<Skin>(initialFixture?.skin ?? "blue");
  let badgeEnabled = $state(initialFixture?.badgeEnabled ?? true);
  let badgePreferenceLoaded = $state(Boolean(initialFixture));
  let now = $state(initialFixture?.now ?? new Date());
  let connectionManager: RadarConnectionManager | null = null;
  let hardRefreshManager: HardRefreshManager | null = null;
  let stopWatchingAuthWindow: (() => void) | null = null;
  let oauthCompletionReceived = false;
  let signInPending = false;
  let signOutPending = false;
  let bootstrapOperation = 0;
  const pushApi: PushApi = {
    settings: () => withAuthExpiry(frontendApi.pushSettings),
    saveSubscription: (subscription) => withAuthExpiry(() => frontendApi.savePushSubscription(subscription)),
    deleteSubscription: () => withAuthExpiry(frontendApi.deletePushSubscription),
    test: () => withAuthExpiry(frontendApi.testPushNotification)
  };
  const pushOperations = createPushOperationCoordinator((snapshot) => {
    pushState = snapshot.state;
    pushMessage = snapshot.message;
  });

  $effect(() => {
    if (!fixture) return;
    bootstrap = fixture.bootstrap;
    bootstrapError = fixture.error ?? null;
    meetings = fixture.meetings ?? [];
    people = fixture.people ?? [];
    radarState = fixture.radarState ?? "ready";
    refreshState = fixture.refreshState ?? "idle";
    connection = fixture.connection ?? "connected";
    settingsOpen = fixture.settingsOpen ?? false;
    settingsState = fixture.settingsState ?? "configured";
    pushState = fixture.pushState ?? "default";
    pushMessage = fixture.pushMessage;
    mode = fixture.mode ?? "dark";
    skin = fixture.skin ?? "blue";
    badgeEnabled = fixture.badgeEnabled ?? true;
    now = fixture.now ?? new Date();
  });

  $effect(() => {
    if (fixture || !badgePreferenceLoaded) return;
    const count = bootstrap?.status === "ready" ? activeMeetingCount(meetings) : 0;
    void syncAppBadge(navigator, count, badgeEnabled);
  });

  onMount(() => {
    if (legalPage) return;
    if (fixture) return;
    if (consumeOAuthCompletion()) return;
    lifecycle.mount();
    connection = navigator.onLine ? "reconnecting" : "offline";
    mode = readPreference("meeting-radar-mode", ["dark", "light"], "dark") as Mode;
    skin = readPreference("meeting-radar-skin", ["blue", "pink", "purple", "orange", "green"], "blue") as Skin;
    badgeEnabled = readAppBadgePreference(localStorage);
    badgePreferenceLoaded = true;
    const clock = setInterval(() => now = new Date(), 20_000);
    hardRefreshManager = new HardRefreshManager({
      environment: browserHardRefreshEnvironment(),
      loadedGitCommit,
      isDeployEligible: () => bootstrap?.status === "ready" && !settingsOpen && settingsState !== "saving" && !signInPending && !signOutPending && refreshState !== "loading",
      isHourlyEligible: () => isStandaloneDisplayMode() && bootstrap?.status === "ready" && radarState === "ready" && !settingsOpen && !signInPending && !signOutPending && refreshState !== "loading"
    });
    hardRefreshManager.start();
    const handleOAuthCompletion = (event: MessageEvent) => {
      if (!isOAuthCompletionMessage(event, location.origin)) return;
      oauthCompletionReceived = true;
      void refreshAfterOAuth();
    };
    window.addEventListener("message", handleOAuthCompletion);
    const handleOnline = () => {
      if (!bootstrap || bootstrapError) void loadBootstrap();
    };
    window.addEventListener("online", handleOnline);
    void loadBootstrap();
    return () => {
      lifecycle.unmount();
      clearInterval(clock);
      window.removeEventListener("message", handleOAuthCompletion);
      window.removeEventListener("online", handleOnline);
      stopWatchingAuthWindow?.();
      connectionManager?.stop();
      hardRefreshManager?.stop();
      void pushOperations.stopAndDrain();
    };
  });

  function readPreference(key: string, allowed: string[], fallback: string) {
    const value = localStorage.getItem(key);
    return value && allowed.includes(value) ? value : fallback;
  }

  function changeMode(value: Mode) { mode = value; onFixtureChange?.({ mode: value }); localStorage.setItem("meeting-radar-mode", value); }
  function changeSkin(value: Skin) { skin = value; onFixtureChange?.({ skin: value }); localStorage.setItem("meeting-radar-skin", value); }
  function changeBadgeEnabled(value: boolean) { badgeEnabled = value; onFixtureChange?.({ badgeEnabled: value }); writeAppBadgePreference(localStorage, value); }

  async function loadBootstrap() {
    if (fixture) {
      bootstrap = fixture.bootstrap;
      return bootstrap;
    }
    const generation = lifecycle.capture();
    const operation = ++bootstrapOperation;
    bootstrapError = null;
    try {
      const next = normalizeBootstrap(await frontendApi.bootstrap());
      if (!lifecycle.isCurrent(generation) || operation !== bootstrapOperation) return null;
      if (next.status === "signed_out") {
        transitionToSignedOut();
        return next;
      }
      bootstrap = next;
      if (next.status === "ready" || next.status === "unconfigured") pushOperations.resume();
      if (next.status === "ready") await loadRadar();
      else connectionManager?.stop();
      return next;
    } catch (error) {
      if (!lifecycle.isCurrent(generation) || operation !== bootstrapOperation) return null;
      if (isAuthExpired(error)) {
        transitionToSignedOut();
        return null;
      }
      bootstrapError = error instanceof Error ? error.message : "Unable to load the workspace.";
      if (!navigator.onLine) connection = "offline";
      return null;
    }
  }

  function normalizeBootstrap(value: unknown): BootstrapState {
    const raw = value as Awaited<ReturnType<typeof frontendApi.bootstrap>>;
    if (!raw.authenticated || !raw.user) return { status: "signed_out" };
    if (!raw.supportedAccount || !raw.organization || !raw.role) {
      return { status: "unsupported_account", user: raw.user };
    }
    const integration = raw.integration ?? {
      configured: false, validationState: "unconfigured", signingConfigured: false,
      webhookRegistered: false, validatedAt: null, lastSynchronizedAt: null
    };
    return integration.configured
      ? { status: "ready", user: raw.user, organizationId: raw.organization.id, role: raw.role, integration }
      : { status: "unconfigured", user: raw.user, organizationId: raw.organization.id, role: raw.role, integration };
  }

  async function loadRadar() {
    if (fixture) {
      meetings = fixture.meetings ?? [];
      people = fixture.people ?? [];
      radarState = "ready";
      bootstrapError = null;
      return;
    }
    const generation = lifecycle.capture();
    ensureConnectionManager();
    radarState = "loading";
    try {
      const state = parseRadarState(await frontendApi.state());
      if (!lifecycle.isCurrent(generation)) return;
      if (!state) throw new Error("Malformed radar state");
      applyRadarState(state);
    } catch (error) {
      if (!lifecycle.isCurrent(generation)) return;
      if (isAuthExpired(error)) {
        transitionToSignedOut();
        return;
      }
      bootstrapError = error instanceof Error ? error.message.replace(/^\d+:/, "") : "Meeting activity could not be loaded.";
      radarState = meetings.length || people.length ? "stale" : "error";
      if (!navigator.onLine) connection = "offline";
    }
  }

  function ensureConnectionManager() {
    if (!connectionManager) {
      connectionManager = new RadarConnectionManager({
        createSocket: createRadarEventsSocket,
        environment: browserRadarConnectionEnvironment(),
        onConnection: (next) => {
          connection = next;
          if (next !== "connected" && radarState === "ready") radarState = "stale";
        },
        onState: (next) => {
          if (bootstrap?.status === "ready") applyRadarState(next);
        },
        onVersion: (version) => hardRefreshManager?.observeServerVersion(version),
        reconcile: reconcileRadar
      });
    }
    connectionManager.start();
  }

  async function reconcileRadar() {
    if (fixture || bootstrap?.status !== "ready" || !navigator.onLine) return;
    const generation = lifecycle.capture();
    try {
      const state = parseRadarState(await frontendApi.state());
      if (!lifecycle.isCurrent(generation)) return;
      if (!state) throw new Error("Malformed radar state");
      applyRadarState(state);
    } catch (error) {
      if (!lifecycle.isCurrent(generation)) return;
      if (isAuthExpired(error)) {
        transitionToSignedOut();
        return;
      }
      if (radarState === "ready") radarState = "stale";
    }
  }

  function applyRadarState(next: RadarStateResponse): boolean {
    if (!radarSequence.accept(next)) return false;
    meetings = next.meetings;
    people = next.people;
    radarState = "ready";
    bootstrapError = null;
    hardRefreshManager?.evaluate();
    return true;
  }

  function transitionToSignedOut() {
    lifecycle.invalidate();
    bootstrapOperation += 1;
    bootstrap = { status: "signed_out" };
    bootstrapError = null;
    radarSequence.reset();
    settingsOpen = false;
    signInPending = false;
    signOutPending = false;
    connectionManager?.stop();
    void pushOperations.stopAndDrain();
  }

  async function withAuthExpiry<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (isAuthExpired(error)) transitionToSignedOut();
      throw error;
    }
  }

  async function signIn() {
    if (fixture || signInPending) return;
    const generation = lifecycle.capture();
    signInPending = true;
    oauthCompletionReceived = false;
    stopWatchingAuthWindow?.();
    stopWatchingAuthWindow = null;
    try {
      const result = await launchGoogleOAuth({
        standalone: isStandaloneDisplayMode(),
        requestSignIn: frontendApi.signIn,
        openAuthWindow: (url, target, features) => window.open(url, target, features),
        navigateCurrent: (url) => location.assign(url)
      });
      if (!lifecycle.isCurrent(generation)) return;
      if (result.kind === "refresh") await loadBootstrap();
      if (result.kind === "popup" && !oauthCompletionReceived) {
        stopWatchingAuthWindow = watchOAuthPopup(result.popup, () => void refreshAfterOAuth());
      }
    } catch (error) {
      if (lifecycle.isCurrent(generation)) bootstrapError = error instanceof Error ? error.message : "Sign in failed.";
    } finally {
      if (lifecycle.isCurrent(generation)) {
        signInPending = false;
        hardRefreshManager?.evaluate();
      }
    }
  }

  async function refreshAfterOAuth() {
    stopWatchingAuthWindow?.();
    stopWatchingAuthWindow = null;
    await loadBootstrap();
  }

  async function signOut() {
    if (fixture || signOutPending) {
      if (!fixture) return;
      bootstrap = { status: "signed_out" };
      settingsOpen = false;
      onFixtureChange?.({ bootstrap, settingsOpen });
      return;
    }
    lifecycle.invalidate();
    bootstrapOperation += 1;
    const generation = lifecycle.capture();
    signOutPending = true;
    try {
      await signOutWithPushCleanup(pushApi, frontendApi.signOut, browserPush(), localStorage, pushOperations.stopAndDrain);
    } finally {
      if (lifecycle.isCurrent(generation)) {
        signOutPending = false;
        transitionToSignedOut();
      }
    }
  }

  async function openSettings() {
    settingsOpen = true;
    if (fixture) onFixtureChange?.({ settingsOpen: true });
    if (fixture || (bootstrap?.status !== "ready" && bootstrap?.status !== "unconfigured")) return;
    void loadPushSettings();
    if (bootstrap.status !== "ready") return;
    const generation = lifecycle.capture();
    try {
      const integration = await frontendApi.settings();
      if (lifecycle.isCurrent(generation) && bootstrap?.status === "ready") bootstrap = { ...bootstrap, integration };
    } catch (error) {
      if (lifecycle.isCurrent(generation) && isAuthExpired(error)) transitionToSignedOut();
      // The settings screen remains usable with the bootstrap status.
    }
  }

  async function loadPushSettings() {
    const ownerKey = currentPushOwnerKey();
    if (!ownerKey || signOutPending) return;
    await pushOperations.run("loading", () => inspectPushNotifications(pushApi, ownerKey, browserPush()));
  }

  async function enablePush() {
    const ownerKey = currentPushOwnerKey();
    if (fixture || !ownerKey || signOutPending) return;
    await pushOperations.run("subscribing", () => enablePushNotifications(pushApi, ownerKey, browserPush()));
  }

  async function disablePush() {
    if (fixture || signOutPending) return;
    await pushOperations.run("subscribing", () => disablePushNotifications(pushApi, browserPush()));
  }

  async function testPush() {
    if (fixture || signOutPending) return;
    await pushOperations.run("testing", () => testPushNotifications(pushApi));
  }

  function currentPushOwnerKey(): string | null {
    if (bootstrap?.status !== "ready" && bootstrap?.status !== "unconfigured") return null;
    return pushOwnerKey(bootstrap.user.id, bootstrap.organizationId);
  }

  async function refreshRadar() {
    if (refreshState === "loading") return;
    refreshState = "loading";
    if (fixture && onFixtureChange) {
      onFixtureChange({ refreshState: "loading" });
      return;
    }
    const generation = lifecycle.capture();
    try {
      if (!fixture) {
        const [, health] = await Promise.all([frontendApi.refresh(), frontendApi.health()]);
        if (!lifecycle.isCurrent(generation)) return;
        hardRefreshManager?.observeServerVersion(health.build);
      }
      if (!fixture && !lifecycle.isCurrent(generation)) return;
      refreshState = "idle";
    } catch (error) {
      if (!lifecycle.isCurrent(generation)) return;
      if (isAuthExpired(error)) {
        transitionToSignedOut();
        return;
      }
      refreshState = "error";
    } finally {
      if (fixture || lifecycle.isCurrent(generation)) hardRefreshManager?.evaluate();
    }
  }

  async function saveSettings(value: IntegrationUpdate) {
    if (fixture) {
      settingsState = "success";
      onFixtureChange?.({ settingsState: "success" });
      return;
    }
    settingsState = "saving";
    const generation = lifecycle.capture();
    try {
      const saved = await frontendApi.saveSettings(value);
      if (!lifecycle.isCurrent(generation)) return;
      if (bootstrap?.status === "ready") bootstrap = { ...bootstrap, integration: saved };
      if (bootstrap?.status === "unconfigured") {
        await loadBootstrap();
        if (!lifecycle.isCurrent(generation)) return;
      }
      settingsState = "success";
      setTimeout(() => { if (lifecycle.isCurrent(generation) && settingsState === "success") settingsState = "configured"; }, 2_000);
    } catch (error) {
      if (!lifecycle.isCurrent(generation)) return;
      if (isAuthExpired(error)) {
        transitionToSignedOut();
        return;
      }
      const message = error instanceof Error ? error.message : "";
      settingsState = message.startsWith("401:") || message.startsWith("403:") ? "unauthorized" : message.startsWith("400:") ? "validation-error" : "server-error";
    } finally {
      if (lifecycle.isCurrent(generation)) hardRefreshManager?.evaluate();
    }
  }

  function closeSettings() {
    settingsOpen = false;
    if (fixture) onFixtureChange?.({ settingsOpen: false });
    hardRefreshManager?.evaluate();
  }

  function configureWorkspace() {
    settingsState = "empty";
    settingsOpen = true;
    onFixtureChange?.({ settingsState: "empty", settingsOpen: true });
  }
</script>

{#if legalPage}
  <LegalScreen page={legalPage} />
{:else if bootstrapError && !bootstrap}
  <AuthScreen state={connection === "offline" ? "offline" : "error"} {now} {mode} {skin} onRetry={() => void loadBootstrap()} onModeChange={changeMode} onSkinChange={changeSkin} />
{:else if !bootstrap}
  <AuthScreen state="loading" {now} {mode} {skin} onModeChange={changeMode} onSkinChange={changeSkin} />
{:else if bootstrap.status === "signed_out"}
  <AuthScreen state="sign-in" {now} {mode} {skin} onSignIn={() => void signIn()} onModeChange={changeMode} onSkinChange={changeSkin} />
{:else if bootstrap.status === "unsupported_account"}
  <AuthScreen state="unsupported" user={bootstrap.user} {now} {mode} {skin} onSignOut={() => void signOut()} onModeChange={changeMode} onSkinChange={changeSkin} />
{:else if settingsOpen && (bootstrap.status === "ready" || bootstrap.status === "unconfigured")}
  <SettingsScreen state={settingsState} role={bootstrap.role} integration={bootstrap.integration} user={bootstrap.user} {pushState} {pushMessage} {badgeEnabled} {now} {mode} {skin} onBack={closeSettings} onSave={(value) => void saveSettings(value)} onEnablePush={() => void enablePush()} onDisablePush={() => void disablePush()} onTestPush={() => void testPush()} onBadgeEnabledChange={changeBadgeEnabled} onSignOut={() => void signOut()} onModeChange={changeMode} onSkinChange={changeSkin} />
{:else if bootstrap.status === "unconfigured"}
  <AuthScreen state="unconfigured" user={bootstrap.user} canConfigure={bootstrap.role === "admin"} {now} {mode} {skin} onConfigure={configureWorkspace} onSignOut={() => void signOut()} onModeChange={changeMode} onSkinChange={changeSkin} />
{:else}
  <RadarStateScreen state={radarState} {meetings} {people} {now} {mode} {skin} {connection} {refreshState} buildGitCommit={loadedGitCommit} message={bootstrapError ?? undefined} onRetry={() => void loadRadar()} onRefresh={() => void refreshRadar()} onSettings={() => void openSettings()} onModeChange={changeMode} onSkinChange={changeSkin} />
{/if}

{#if !legalPage}<PortraitGuard />{/if}
